import { useEffect, useState } from "react";
import { assetUrl } from "../api";
import { t } from "../i18n";

interface Props {
  dadName: string;
  styleLabel: string;
  /** Widget mode (PDP-embedded) shows just the progress bar + headline.
   *  The reaction carousel + testimonials are dropped — the PDP already
   *  provides context (product images, reviews section, trust badges). */
  widgetMode?: boolean;
}

// Curated preview slides shown while generating. We show blockbuster-quality
// Firefighter base images here to set high expectations and build anticipation.
const REACTIONS = [
  {
    img: "/styles/american_hero.png",
    label: "The Patriot",
  },
  {
    img: "/styles/fathers_embrace.png",
    label: "Father's Embrace",
  },
  {
    img: "/styles/sacred_duty.png",
    label: "Sacred Duty",
  },
  {
    img: "/styles/k9_rescue.png",
    label: "K-9 Rescue",
  },
];

const TESTIMONIALS = [
  { quote: "My dad is a retired firefighter of 30 years. When he unwrapped the canvas, he was completely speechless. Best gift ever.", name: "Sarah L." },
  { quote: "I cried when I saw it — it captured his spirit perfectly. Now it has the place of honor in our living room.", name: "Jessica T." },
  { quote: "He immediately hung the poster in his man cave. I've never seen my dad so proud!", name: "Mike D." },
  { quote: "My dad passed away last year. Getting this canvas made us feel like he's still watching over us. This is priceless.", name: "Mark R." },
  { quote: "The quality of the canvas is outstanding. My husband was so surprised, he showed it to all his station buddies.", name: "Emily R." },
  { quote: "Got this for Father's Day. He laughed, then teared up. Truly a masterpiece we'll keep forever.", name: "David W." },
  { quote: "Unbelievable detail. My dad couldn't believe it was him looking like a true movie hero.", name: "Amanda K." },
  { quote: "The canvas arrived perfectly packaged. Seeing the look of pure joy on my dad's face was worth every penny.", name: "Chloe M." },
];

// Fal generation typically completes in 10–18s. We animate to 95% over this
// duration; after that we creep 95→99 slowly so the bar never visually
// "freezes" if Fal queue takes 30-60s. The screen unmounts when the parent's
// poll detects "ready", giving the user the "97% then poof, preview" moment.
const EXPECTED_DURATION_MS = 14000;

const CAROUSEL_INTERVAL_MS = 2800;

export function LoadingStep({ dadName, styleLabel, widgetMode }: Props) {
  const [pct, setPct] = useState(0);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  // Preload all reaction images so the carousel doesn't flash white between rotations.
  useEffect(() => {
    REACTIONS.forEach((r) => {
      const img = new Image();
      img.src = assetUrl(r.img);
    });
  }, []);

  // Two-phase progress:
  //   Phase 1 (0–14s):  0 → 95%  (steady rise)
  //   Phase 2 (14s+):  95 → 99%  (asymptotic creep, never reaches 100% until ready)
  // Why: face-swap occasionally takes 30-60s; a flat 95% bar feels broken.
  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      let ratio: number;
      if (elapsed < EXPECTED_DURATION_MS) {
        ratio = (elapsed / EXPECTED_DURATION_MS) * 0.95;
      } else {
        const overflow = (elapsed - EXPECTED_DURATION_MS) / 1000;
        // +0.04 over ~120s: 95% at 14s → 97% at 74s → 99% at 134s
        ratio = 0.95 + Math.min(0.04, overflow / 3000);
      }
      setPct(Math.floor(ratio * 100));
    }, 120);
    return () => clearInterval(tick);
  }, []);

  // Auto-rotate the reaction carousel.
  useEffect(() => {
    const t = setInterval(() => {
      setCarouselIdx((i) => (i + 1) % REACTIONS.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Auto-rotate the testimonials
  useEffect(() => {
    const t = setInterval(() => {
      setTestimonialIdx((i) => (i + 2) % TESTIMONIALS.length);
    }, 4500);
    return () => clearInterval(t);
  }, []);

  // Stage-aware headline. Each stage uses an i18n key so the JA register
  // (calm, observational) replaces the EN mythic register at render time
  // when window.MonogatariLocale === "ja". styleLabel is unused in the new
  // copy — preserved as a prop for the legacy non-widget render path.
  const heroName = dadName?.trim() || t("loading.hero_fallback");
  void styleLabel;
  const headline =
    pct < 20
      ? t("loading.stage_1", { name: heroName })
      : pct < 50
      ? t("loading.stage_2", { name: heroName })
      : pct < 80
      ? t("loading.stage_3")
      : pct < 95
      ? t("loading.stage_4")
      : t("loading.stage_5");

  const current = REACTIONS[carouselIdx]!;

  return (
    <div className="card loading-card">
      <div className="loading-top">
        <div className="brand-pulse" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
          </svg>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-meta">{t("loading.percent", { pct })}</div>
      </div>

      <h2 className="loading-headline">{headline}</h2>

      {!widgetMode && (
        <>
          <div className="reaction-carousel">
            <div className="reaction-stage">
              <img
                key={carouselIdx}
                className="reaction-img"
                src={assetUrl(current.img)}
                alt=""
              />
              <span className="reaction-tag">{current.label}</span>
            </div>
            <div className="carousel-dots" aria-hidden>
              {REACTIONS.map((_, i) => (
                <span key={i} className={i === carouselIdx ? "active" : ""} />
              ))}
            </div>
          </div>

          <div className="testimonials" style={{ minHeight: "90px" }}>
            <div key={testimonialIdx} className="animated-testimonial-group">
              {[TESTIMONIALS[testimonialIdx], TESTIMONIALS[(testimonialIdx + 1) % TESTIMONIALS.length]].map((t) => (
                <div key={t?.name} className="testimonial">
                  <span className="t-stars" aria-hidden>★★★★★</span>
                  <span className="t-text">{`“${t?.quote}”`}</span>
                  <span className="t-name">— {t?.name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
